import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  type AppTableFeatures,
  appTableFeatures,
  RevampTable,
  TableCell,
} from '@design/components/table';
import { useScrollListener } from '@helpers/hooks/use-scroll-listener/useScrollListener';
import { isElementScrollbarVisible } from '@helpers/utils/dom/DOM';
import {
  Column,
  ColumnDef,
  flexRender,
  PaginationState,
  useTable,
} from '@tanstack/react-table';
import { SortableTableHeaderCell } from '@terminal/components/sortable-table-header-cell/SortableTableHeaderCell';
import { TableAction } from '@terminal/views/screener/results/screener-table-utils';
import {
  SCENARIO_ENTITY_COLUMN_ID,
  SCENARIO_HISTORY_COLUMN_ID,
  ScenarioTableData,
} from '@terminal/widgets/scenario/utils/scenario-table-utils/scenario-table-utils';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useScenarioStore } from '../../hooks/use-scenario-store/useScenarioStore';
import styles from './ScenarioTable.module.scss';

const REVEALED_ROWS_PAGE_SIZE = 20;

export interface ScenarioTableProps {
  columns: ColumnDef<AppTableFeatures, ScenarioTableData>[];
  data: ScenarioTableData[];
  hasError: boolean;
  isStreaming?: boolean;
  onRowClick: (item: ScenarioTableData) => void;
}

export const ScenarioTable = ({
  columns,
  data,
  hasError,
  isStreaming,
  onRowClick,
}: ScenarioTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize: REVEALED_ROWS_PAGE_SIZE,
  }));
  const {
    activeFilterOptions,
    columnFilters,
    showColumnFilterModal,
    clearColumnFilter,
  } = useScenarioStore(state => ({
    ...state.filterGroups['COLUMN_FILTERS'],
    columnFilters: state.columnFilters,
    showColumnFilterModal: state.showColumnFilterModal,
    clearColumnFilter: state.clearColumnFilter,
  }));

  const table = useTable({
    features: appTableFeatures,
    data,
    columns,
    state: {
      columnFilters,
      pagination,
    },
    defaultColumn: {
      sortUndefined: 'last',
    },
    onPaginationChange: setPagination,
  });
  const { rows } = table.getRowModel();
  const hasHiddenRows = table.getCanNextPage();

  useEffect(() => {
    if (!data.length) {
      setPagination(current => ({
        ...current,
        pageSize: REVEALED_ROWS_PAGE_SIZE,
      }));
    }
  }, [data.length]);

  const revealMoreRows = () =>
    setPagination(current => ({
      ...current,
      pageSize: current.pageSize + REVEALED_ROWS_PAGE_SIZE,
    }));

  useScrollListener(
    containerRef,
    () => {
      if (hasHiddenRows) {
        revealMoreRows();
      }

      return hasHiddenRows;
    },
    [hasHiddenRows],
    { isGlobalScrollListened: false, offset: 20 }
  );

  useLayoutEffect(() => {
    if (!hasHiddenRows || !containerRef.current?.offsetHeight) {
      return;
    }

    if (isElementScrollbarVisible(containerRef, 'vertical')) {
      return;
    }

    revealMoreRows();
  }, [hasHiddenRows, rows.length]);

  const isCellNumber = (columnId: string) =>
    ![SCENARIO_ENTITY_COLUMN_ID, SCENARIO_HISTORY_COLUMN_ID].includes(columnId);

  const handleDropdownAction =
    (column: Column<AppTableFeatures, ScenarioTableData>) =>
    (action: TableAction) => {
      if (action === 'ascending') {
        return column.toggleSorting(false);
      }

      if (action === 'descending') {
        return column.toggleSorting(true);
      }

      if (action === 'clearSort') {
        return column.clearSorting();
      }

      if (action === 'clearFilter') {
        clearColumnFilter(column.id);
      }

      if (action === 'filter' || action === 'editFilter') {
        showColumnFilterModal(column.id);
      }
    };

  const getEnabledKeys = (
    column: Column<AppTableFeatures, ScenarioTableData>
  ) => {
    const enabledKeys: TableAction[] = ['ascending', 'descending'];

    if (column.getIsFiltered()) {
      enabledKeys.push('clearFilter', 'editFilter');
    } else if (column.id !== SCENARIO_ENTITY_COLUMN_ID) {
      enabledKeys.push('filter');
    }

    if (column.getIsSorted()) {
      enabledKeys.push('clearSort');
    }

    return enabledKeys;
  };

  if (hasError) {
    throw new Error('ScenarioTable: useScenarios error');
  }

  return (
    <div className={styles.scenarioTableRoot} ref={containerRef}>
      <RevampTable data-testid="scenario-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <SortableTableHeaderCell
                  key={header.id}
                  header={header}
                  handleAction={handleDropdownAction(header.column)}
                  enabledKeys={getEnabledKeys(header.column)}
                  activeFilterOptions={activeFilterOptions}
                />
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} onClick={() => onRowClick(row.original)}>
              {row.getVisibleCells().map(cell => (
                <TableCell
                  key={cell.id}
                  isNumber={isCellNumber(cell.column.id)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </tr>
          ))}
          {(hasHiddenRows || isStreaming) && !!rows.length && (
            <tr key="loading-row" data-testid="scenario-table-loading-row">
              {rows[0].getVisibleCells().map(cell => (
                <TableCell key={cell.id} isNumber={false}>
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          width: '100%',
                          height: '20px',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              ))}
            </tr>
          )}
        </tbody>
      </RevampTable>
    </div>
  );
};
